import { QUERY_KEYS } from './queryKeys';
import CacheHelper from '#app/helpers/Cache/CacheHelper';

/** Параметры для динамических scope (OBLIGATIONS_EDIT и т.д.) */
export interface RefreshConfigParams {
  clientId?: number | string | null;
  obligationId?: number | string;
  roboId?: number | string;
  currency?: string;
  client?: any;
  activeIds?: number[];
}

/** Конфиг одного scope: queryKeys, cachePrefixes, опционально dispatchActions */
export interface ScopeConfig {
  queryKeys: unknown[];
  cachePrefixes: string[];
  dispatchActions?: string[];
}

/** Конфиг scope — либо объект, либо функция от params */
type ScopeConfigOrFn = ScopeConfig | ((params: RefreshConfigParams) => ScopeConfig);

/**
 * Скоупы инвалидации — одна модалка/мутация = один scope.
 * useDataRefresh(scope).invalidateQueries() инвалидирует queryKeys (TanStack Query), очищает cachePrefixes (IndexedDB)
 * и диспатчит dispatchActions (Redux). Ниже по каждому scope: где вызывается, что обновляется и зачем.
 */
export const InvalidationScope = Object.freeze({
  ACCOUNTS: 'accounts',
  BACKUPS: 'backups',
  WEALTH_WIDGET: 'wealth_widget',
  INSURANCES: 'insurances',
  PLAN_SAVE_QUERY_CACHE: 'plan_save_query_cache',
  GOALS_PLAN: 'goals_plan',
  GOAL_ITEMS: 'goal_items',
  /** Сохранение портфеля цели (распределение активов) */
  GOAL_PORTFOLIO: 'goal_portfolio',
  /** Архивация цели — инвалидирует goals + goalItems (для exceptItems) */
  GOAL_ARCHIVED: 'goal_archived',
  /** Платежи по цели — создание/редактирование/удаление */
  GOAL_PAYMENTS: 'goal_payments',
  TRADE: 'trade',
  TRANSFER: 'transfer',
  OBLIGATIONS_EDIT: 'obligations_edit',
  INCOMES_TACTICS: 'incomes_tactics',
  SPENDINGS_TACTICS: 'spendings_tactics',
  ACTIVE: 'active',
  REPO: 'repo',
  DIVIDENDS: 'dividends',
  COUPONS: 'coupons',
  CATEGORY: 'category',
  TRADE_PAYMENTS: 'trade_payments',
  TRADE_COMMISSIONS: 'trade_commissions',
  TRANSFER_COMMISSIONS: 'transfer_commissions',
  SIMPLE_TRANSFER: 'simple_transfer',
  OBLIGATION_PAYMENTS: 'obligation_payments',
  INSURANCE_ACTIONS: 'insurance_actions',
  ALIMONY: 'alimony',
  CLIENT_SWITCH: 'client_switch',
  /** WebSocket: синк Т-Инвестиции, парсинг отчётов АТОН/БКС */
  BROKER_SYNC: 'broker_sync',
  /** WebSocket: только обновление списка загруженных отчётов АТОН/БКС (после парсинга) */
  BROKER_REPORT_FILES: 'broker_report_files',
  /** WebSocket: синк Zenmoney */
  ZENMONEY_SYNC: 'zenmoney_sync',
  /** Чат: отправка сообщения, set-read, Pusher — список чатов и сообщения (TanStack Query + IndexedDB по префиксу chat_). */
  CHAT: 'chat',
  /** Группы активов: создание/редактирование/удаление группы (GroupModal). */
  ACTIVE_GROUPS: 'active_groups',
  /** Подкатегории доходов/расходов: создание/редактирование/удаление (SubcategoryForm). */
  SUBCATEGORY: 'subcategory',
  /** Клиенты: создание/редактирование (ClientsCreate, ClientsEdit). */
  CLIENT: 'client',
  /** Робоэдвайзер: общий scope (подключение/обновление) со статусом и деталью по id. */
  ROBOADVISOR: 'roboadvisor',
  /** Робоэдвайзер: отключение подписки. Обновляет списки, но не дергает удаленные detail/status по roboId. */
  ROBOADVISOR_DISCONNECT: 'roboadvisor_disconnect',
  /** Робоэдвайзер: вывод средств (WithdrawModal). Деталь, операции робо, баланс, нотификации + logs/creditLog (общий журнал). */
  ROBOADVISOR_WITHDRAW: 'roboadvisor_withdraw',
  /** Робоэдвайзер: сохранение данных подписки (ContractStep). Инвалидирует паспортные данные. */
  ROBOADVISOR_USER_PASSPORT: 'roboadvisor_user_passport',
  /** Робоэдвайзер: только инвалидация user-passport, без dispatch getUser */
  ROBOADVISOR_USER_PASSPORT_QUERY_ONLY: 'roboadvisor_user_passport_query_only',
  /** Создание кастомного банка «на лету» в форме счёта — обновление только списка banks по clientId. */
  BANKS_LIST: 'banks_list',
  /** Создание кастомного брокера «на лету» в форме счёта — обновление только списка brokers по clientId. */
  BROKERS_LIST: 'brokers_list',
  /** Интеграции: привязка финансового советника (profile/bind-manager). Без инвалидации all-tokens. */
  ADVISOR_BIND: 'advisor_bind',
  /** Интеграции: после удаления субсчёта — счета, инвестиции/архив/репо/переводы, доходы/расходы/тактика, дивиденды/купоны/календарь, цели, обязательства, имущество, страхования, уведомления, кеши балансов/портфеля. */
  INTEGRATIONS_DELETE_ACCOUNT: 'integrations_delete_account',
  /** Интеграции: список токенов (profile/all-tokens) для блоков Tinkoff/BCS/Freedom/Zenmoney. */
  INTEGRATION_TOKENS: 'integration_tokens',
  /** WebSocket: точечный апдейт инвестиций по массиву activeIds без полной инвалидации списка. */
  INVESTS_ACTIVES_UPDATED: 'invests_actives_updated',
} as const);

export type InvalidationScopeValue = (typeof InvalidationScope)[keyof typeof InvalidationScope];

/**
 * Конфиг по scope: queryKeys (TanStack Query), cachePrefixes (IndexedDB), dispatchActions (имена экшенов из useDataRefresh).
 * Значение — объект { queryKeys, cachePrefixes, dispatchActions? } или функция (params) => { ... }.
 * Важно: после очистки cachePrefixes следующий флоу обновления виджета благосостояния идет через
 * InvestsProvider (реакция на изменения client/currency/accounts/courses и данные после query invalidation/getUserAccounts),
 * без отдельного dispatch handleReloadBalance.
 *
 * По каждому scope (для ревью):
 *
 * ACCOUNTS
 *   Вызывается: useAccountMutations → AccountModal (создание/редактирование/удаление/восстановление счёта).
 *   Обновляется: accounts, invests, logs (TanStack Query); BALANCE_ACCOUNTS, BALANCE_INVESTS (IndexedDB, благосостояние); resetAvailableTypes, getUserAccounts (Redux).
 *   Зачем: список счетов и инвестиций перезапрашивается; виджет благосостояния и фильтр типов счетов обновляются.
 *
 * BACKUPS
 *   Вызывается: Backup.jsx (Config) — после создания точки восстановления, восстановления из точки, удаления точки.
 *   Обновляется: backups (queries); BALANCE_ACCOUNTS (IndexedDB); getUserAccounts (Redux).
 *   Зачем: список точек + согласованность баланса и счетов после restore.
 *
 * INSURANCES
 *   Вызывается: useInsuranceMutations, InsuranceFormModal (план) — создание/редактирование/удаление страхования на странице плана.
 *   Обновляется: insurances + accounts (queries); BALANCE_ACCOUNTS (IndexedDB); getUserAccounts (Redux).
 *   Зачем: после операций ИСЖ/НСЖ обновляем и список счетов (создается/меняется страховой счет), и список страхований.
 *
 * GOALS_PLAN
 *   Вызывается: useGoalMutations (GoalForm) — создание/редактирование/удаление цели.
 *   Обновляется: goals (queries); TACTICS_MONTH (IndexedDB).
 *   Зачем: список целей и тактика по месяцам перезапрашиваются; salary-plan получает актуальные данные через RQ.
 *
 * GOAL_ITEMS
 *   Вызывается: useGoalItemsMutations — привязка/отвязка активов и счетов к цели.
 *   Обновляется: goals, goalItems (queries).
 *   Зачем: список целей и привязанные элементы перезапрашиваются.
 *
 * GOAL_PORTFOLIO
 *   Вызывается: useSavePortfolioPlan (GoalPlansForm) — сохранение портфеля цели (распределение активов).
 *   Обновляется: goals (queries).
 *   Зачем: данные целей перезапрашиваются для отображения нового портфеля.
 *
 * GOAL_PAYMENTS
 *   Вызывается: useActiveGoalPaymentActions — создание/редактирование/удаление платежей по цели.
 *   Обновляется: goalItems (queries); BALANCE_ACCOUNTS (IndexedDB); getUserAccounts (Redux).
 *   Зачем: список платежей цели перезапрашивается; баланс по счетам в кеше и виджет согласованы с операциями по цели.
 *
 * GOAL_ARCHIVED
 *   Вызывается: useGoalArchivedMutation — архивация (закрытие) цели.
 *   Обновляется: goals, goalItems (queries); BALANCE_ACCOUNTS, TACTICS_MONTH (IndexedDB).
 *   Зачем: список целей обновляется; goalItems помечаются устаревшими для корректного отображения exceptItems в других целях.
 *
 * TRADE
 *   Вызывается: ActiveFormWrapper (TradeFormModal), InvestHeader, ArchiveInvestHeaders — покупка/продажа актива.
 *   Обновляется: accounts, invests, archive, repos, logs, dashboard.portfolioPies (TanStack Query); BALANCE_* (IndexedDB, благосостояние); getUserAccounts (Redux).
 *   Зачем: счета, инвестиции, архив, репо, логи и балансы перезапрашиваются/очищаются; виджет благосостояния обновляется.
 *
 * TRANSFER
 *   Вызывается: baseActions (TransferFormWrapper) — доход или расход (трансфер между счетами).
 *   Обновляется: accounts, incomes, spendings, tactics, logs (queries); BALANCE_ACCOUNTS (IndexedDB); getUserAccounts (Redux).
 *   Зачем: счета, доходы, расходы, тактика, логи перезапрашиваются; виджет благосостояния обновляется.
 *
 * OBLIGATIONS_EDIT
 *   Вызывается: useObligationMutations (ObligationFormModal) — редактирование обязательства (кредит, займ). Параметр: obligationId.
 *   Обновляется: accounts, obligations, logs + detail/payments по obligationId (queries); BALANCE_OBLIGATIONS, BALANCE_ACCOUNTS (IndexedDB); getUserAccounts (Redux).
 *   Зачем: счета, обязательства и связанные детали перезапрашиваются; данные salary-plan обновляются через RQ-инвалидацию.
 *
 * INCOMES_TACTICS
 *   Вызывается: useIncomeMutations (форма дохода в TransferFormWrapper), создание/редактирование/удаление дохода.
 *   Обновляется: accounts, incomes, tactics, logs (queries); BALANCE_ACCOUNTS (IndexedDB); getUserAccounts (Redux).
 *   Зачем: счета, доходы, тактика, логи перезапрашиваются; виджет обновляется.
 *
 * SPENDINGS_TACTICS
 *   Вызывается: useSpendingMutations, Spendings.jsx, TacticsTypes.jsx — создание/редактирование/удаление расхода, сохранение бюджета.
 *   Обновляется: accounts, spendings, tactics, logs (queries); BALANCE_ACCOUNTS (IndexedDB); getUserAccounts (Redux).
 *   Зачем: счета, расходы, тактика, логи перезапрашиваются; виджет обновляется.
 *
 * ACTIVE
 *   Вызывается: ActiveFormWrapper (ActiveFormModal), useValuationActions, useActiveFundItemActions — добавление/редактирование/удаление актива.
 *   Обновляется: invests, archive, repos, properties, logs (TanStack Query); BALANCE_* (IndexedDB, благосостояние); getUserAccounts (Redux).
 *   Зачем: инвестиции, архив, репо, журнал операций (logs), список активов на Счета/Активы/Обязательства перезапрашиваются; виджет обновляется.
 *
 * REPO
 *   Вызывается: useRepoActions, useRepoCloseActions (RepoCreateModal, RepoCloseModal) — создание/закрытие репо.
 *   Обновляется: accounts, invests, archive, repos, logs, dashboard.portfolioPies (TanStack Query); BALANCE_* (IndexedDB, благосостояние); getUserAccounts (Redux).
 *   Зачем: счета, инвестиции, архив, репо, журнал операций перезапрашиваются; виджет обновляется.
 *
 * DIVIDENDS
 *   Вызывается: useDividendActions (DividendForm в ActiveFormModal) — дивиденды по активу.
 *   Обновляется: accounts, invests, archive, repos, logs, dashboard.portfolioPies (TanStack Query); BALANCE_* (IndexedDB, благосостояние); getUserAccounts (Redux).
 *   Зачем: счета, инвестиции, архив, репо, логи перезапрашиваются; виджет обновляется.
 *
 * COUPONS
 *   Вызывается: useCouponActions (CouponForm в ActiveFormModal) — купон по облигации.
 *   Обновляется: accounts, invests, archive, repos, logs, dashboard.portfolioPies (TanStack Query); BALANCE_* (IndexedDB, благосостояние); getUserAccounts (Redux).
 *   Зачем: то же, что DIVIDENDS.
 *
 * CATEGORY
 *   Вызывается: useCategoryActions (IncomeCategoryForm, SpendingCategoryForm) — категория дохода/расхода (название, цвет).
 *   Обновляется: tactics (queries). cachePrefixes и dispatchActions нет.
 *   Зачем: только список тактик (категорий) перезапрашивается; счета и операции не меняются.
 *
 * TRADE_PAYMENTS
 *   Вызывается: useTradePaymentActions (TradePaymentForm) — платёж по сделке (ввод/вывод).
 *   Обновляется: accounts, invests, archive, repos, logs, dashboard.portfolioPies (TanStack Query); BALANCE_* (IndexedDB, благосостояние); getUserAccounts (Redux).
 *   Зачем: счета, инвестиции, архив, репо, логи перезапрашиваются; виджет обновляется.
 *
 * TRADE_COMMISSIONS
 *   Вызывается: useTradeCommissionMutations (TradeCommissionForm) — комиссия по сделке (покупка/продажа бумаги).
 *   Обновляется: только список комиссий по сделке (tradeCommissions) и виджет благосостояния (cabinetValue).
 *   Зачем: без лишних обновлений accounts/invests/logs; список обновляется по факту операции, не по закрытию модалки.
 *
 * TRANSFER_COMMISSIONS
 *   Вызывается: useTransferCommissionMutations (TransferCommissionForm) — комиссия по переводу счетов.
 *   Обновляется: только список комиссий по переводу (transferCommissions) и виджет благосостояния (cabinetValue).
 *   Зачем: без лишних обновлений accounts/invests/logs; список обновляется по факту операции, не по закрытию модалки.
 *
 * SIMPLE_TRANSFER
 *   Вызывается: useSimpleTransferActions (SimpleTransferModal) — простой перевод между счетами.
 *   Обновляется: accounts, invests, logs (TanStack Query); BALANCE_ACCOUNTS, BALANCE_INVESTS (IndexedDB, благосостояние); getUserAccounts (Redux).
 *   Зачем: счета, инвестиции, логи перезапрашиваются; виджет обновляется.
 *
 * OBLIGATION_PAYMENTS
 *   Вызывается: useObligationPaymentActions (ObligationPaymentForm) — платёж по обязательству.
 *   Обновляется: accounts, obligations, logs (queries); BALANCE_OBLIGATIONS, BALANCE_ACCOUNTS (IndexedDB); getUserAccounts (Redux).
 *   Зачем: счета, обязательства, логи перезапрашиваются; виджет обновляется.
 *
 * INSURANCE_ACTIONS
 *   Вызывается: useInsuranceActions (InsuranceForm внутри ActiveFormModal) — создание/редактирование/удаление страхового актива.
 *   Обновляется: accounts, insurances, invests, logs (TanStack Query); BALANCE_PROPERTIES, BALANCE_ACCOUNTS (IndexedDB, благосостояние); getUserAccounts (Redux).
 *   Зачем: счета, страхования, инвестиции, логи перезапрашиваются; виджет обновляется.
 *
 * ALIMONY
 *   Вызывается: useAlimonyMutations (форма алиментов) — создание/редактирование/удаление алиментов.
 *   Обновляется: accounts, obligations, logs (queries); BALANCE_OBLIGATIONS, BALANCE_ACCOUNTS (IndexedDB); getUserAccounts (Redux).
 *   Зачем: счета, обязательства, логи перезапрашиваются; виджет обновляется.
 *
 * CLIENT_SWITCH
 *   Вызывается: Header.jsx, MobileMenu.tsx — при смене клиента в шапке (менеджер/владелец выбирает другого клиента).
 *   Обновляется: queryKeys здесь пустые (глобальный queryClient.clear() выполняется в refreshAllUserData до invalidateByScope);
 *   локально очищаем BALANCE_ACCOUNTS (IndexedDB); dispatchActions: getUser, getAtonFiles (Redux).
 *
 * BROKER_SYNC
 *   Вызывается: webSocketMiddleware — после синка Т-Инвестиции, Freedom, БКС API или парсинга отчёта АТОН/БКС (файл).
 *   Обновляется: accounts, invests, archive, repos, logs, notifications, creditLog, cabinetValue, dashboard.portfolioPies (TanStack Query); BALANCE_INVESTS, BALANCE_ACCOUNTS (IndexedDB, благосостояние); getUserNotifications, getUserAccounts (Redux).
 *   Параметр: clientId (для getUserNotifications).
 *
 * BROKER_REPORT_FILES
 *   Вызывается: webSocketMiddleware — только после парсинга отчёта АТОН/БКС (не после Т-Инвестиции). Один вызов вместо двух.
 *   Обновляется: те же queryKeys/cachePrefixes что BROKER_SYNC; getAtonFiles + getUserNotifications + getUserAccounts (Redux).
 *
 * ZENMONEY_SYNC
 *   Вызывается: webSocketMiddleware — после синка Zenmoney.
 *   Обновляется: incomes, spendings, tactics + accounts и QUERY_KEYS_BROKER_SYNC (invests, archive, repos, logs, notifications, creditLog, cabinetValue) (queries); TACTICS_MONTH + кеши как у BROKER_SYNC (IndexedDB); getUserNotifications, getUserAccounts (Redux).
 *   Параметр: clientId (для getUserNotifications).
 *
 * ADVISOR_BIND
 *   Вызывается: BindAdvisorBlock — после успешного profile/bind-manager.
 *   Обновляется: BALANCE_ACCOUNTS (IndexedDB); getUser, getUserAccounts (Redux); TanStack Query не инвалидируем.
 *   Зачем: обновить клиента и владельца/советника и сбросить кеш баланса по счетам.
 *
 * INTEGRATIONS_DELETE_ACCOUNT
 *   Вызывается: DeleteAccountBlock — после успешного user-account-currency/remove.
 *   Обновляется: integrationTokens; QUERY_KEYS_INVESTS_WITH_LOGS (accounts, invests, archive, repos, logs, creditLog, cabinetValue, transferTrades*, dashboard.portfolioPies); incomes, spendings, tactics; dividends, coupons, calendarPayments; obligations, properties, goals, goalItems, insurances, activePayments, notifications (TanStack Query); BALANCE_ACCOUNTS/BALANCE_INVESTS/TACTICS_MONTH/BALANCE_OBLIGATIONS/BALANCE_PROPERTIES (IndexedDB, благосостояние и тактика); resetAvailableTypes, getUserAccounts (Redux).
 *   Зачем: субсчёт привязан к операциям и сущностям (активы, цели, обязательства, дивиденды/купоны и т.д.) — широкая инвалидация как при затрагивании счёта в сделках/синке.
 */

/** Повторяющиеся наборы queryKeys */
const QUERY_KEYS_INVESTS_WITH_LOGS = [
  QUERY_KEYS.accounts.base(),
  QUERY_KEYS.invests.base(),
  QUERY_KEYS.dashboard.portfolioPiesBase(),
  QUERY_KEYS.archive.base(),
  QUERY_KEYS.repos.base(),
  QUERY_KEYS.logs.base(),
  QUERY_KEYS.creditLog.base(),
  QUERY_KEYS.cabinetValue.base(),
  QUERY_KEYS.transferTrades.base(),
  QUERY_KEYS.transferTradesToApi.base(),
];

const QUERY_KEYS_ACCOUNTS_INVESTS_LOGS = [
  QUERY_KEYS.accounts.base(),
  QUERY_KEYS.invests.base(),
  QUERY_KEYS.logs.base(),
  QUERY_KEYS.creditLog.base(),
];

const QUERY_KEYS_ACCOUNTS_OBLIGATIONS_LOGS = [
  QUERY_KEYS.accounts.base(),
  QUERY_KEYS.obligations.base(),
  QUERY_KEYS.logs.base(),
  QUERY_KEYS.creditLog.base(),
];

/** IndexedDB: кеш виджета благосостояния (InvestsProvider), не /invest и не pie дашборда */
const CACHE_WELLBEING_BALANCE = [
  CacheHelper.BALANCE_ACCOUNTS_PREFIX,
  CacheHelper.BALANCE_INVESTS_PREFIX,
];

const CACHE_BALANCE_ACCOUNTS = [CacheHelper.BALANCE_ACCOUNTS_PREFIX];

/** BROKER_SYNC / парсинг отчётов: accounts, invests, archive, repos, logs, notifications, creditLog, cabinetValue */
const QUERY_KEYS_BROKER_SYNC = [
  QUERY_KEYS.accounts.base(),
  QUERY_KEYS.invests.base(),
  QUERY_KEYS.archive.base(),
  QUERY_KEYS.repos.base(),
  QUERY_KEYS.transferTrades.base(),
  QUERY_KEYS.transferTradesToApi.base(),
  QUERY_KEYS.logs.base(),
  QUERY_KEYS.notifications.base(),
  QUERY_KEYS.creditLog.base(),
  QUERY_KEYS.cabinetValue.base(),
];

const CACHE_BROKER_SYNC = [CacheHelper.BALANCE_INVESTS_PREFIX, CacheHelper.BALANCE_ACCOUNTS_PREFIX];

const CACHE_BALANCE_ALL_PARTS = [
  CacheHelper.BALANCE_ACCOUNTS_PREFIX,
  CacheHelper.BALANCE_INVESTS_PREFIX,
  CacheHelper.BALANCE_PROPERTIES_PREFIX,
  CacheHelper.BALANCE_OBLIGATIONS_PREFIX,
];

const SCOPE_CONFIG: Record<string, ScopeConfigOrFn> = {
  [InvalidationScope.ACCOUNTS]: {
    queryKeys: [
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.invests.base(),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.cabinetValue.base(),
    ],
    cachePrefixes: CACHE_WELLBEING_BALANCE,
    dispatchActions: ['resetAvailableTypes', 'getUserAccounts'],
  },
  [InvalidationScope.BACKUPS]: {
    queryKeys: [QUERY_KEYS.backups.base()],
    cachePrefixes: CACHE_BALANCE_ALL_PARTS,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.WEALTH_WIDGET]: {
    queryKeys: [QUERY_KEYS.cabinetValue.base()],
    cachePrefixes: CACHE_BALANCE_ALL_PARTS,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.INSURANCES]: {
    queryKeys: [QUERY_KEYS.insurances.base(), QUERY_KEYS.accounts.base()],
    cachePrefixes: CACHE_BALANCE_ACCOUNTS,
    dispatchActions: ['getUserAccounts'],
  },
  // PLAN_SAVE_QUERY_CACHE is query/cache-only and powers SalaryPlan refresh without Redux getPlan chain.
  [InvalidationScope.PLAN_SAVE_QUERY_CACHE]: {
    queryKeys: [QUERY_KEYS.plan.base(), QUERY_KEYS.tactics.base(), QUERY_KEYS.goals.base()],
    cachePrefixes: [CacheHelper.TACTICS_MONTH_PREFIX],
  },
  [InvalidationScope.GOALS_PLAN]: {
    queryKeys: [QUERY_KEYS.goals.base(), QUERY_KEYS.goalItems.base()],
    cachePrefixes: [CacheHelper.TACTICS_MONTH_PREFIX],
    dispatchActions: [],
  },
  [InvalidationScope.GOAL_ITEMS]: {
    queryKeys: [QUERY_KEYS.goals.base(), QUERY_KEYS.goalItems.base()],
    cachePrefixes: [],
    dispatchActions: [],
  },
  [InvalidationScope.GOAL_PORTFOLIO]: {
    queryKeys: [QUERY_KEYS.goals.base()],
    cachePrefixes: [],
    dispatchActions: [],
  },
  [InvalidationScope.GOAL_ARCHIVED]: {
    queryKeys: [QUERY_KEYS.goals.base(), QUERY_KEYS.goalItems.base()],
    cachePrefixes: [...CACHE_BALANCE_ACCOUNTS, CacheHelper.TACTICS_MONTH_PREFIX],
    dispatchActions: [],
  },
  [InvalidationScope.GOAL_PAYMENTS]: {
    queryKeys: [QUERY_KEYS.goals.base(), QUERY_KEYS.goalItems.base()],
    cachePrefixes: CACHE_BALANCE_ACCOUNTS,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.TRADE]: {
    queryKeys: [...QUERY_KEYS_INVESTS_WITH_LOGS],
    cachePrefixes: [...CACHE_WELLBEING_BALANCE],
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.TRANSFER]: {
    queryKeys: [
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.incomes.base(),
      QUERY_KEYS.spendings.base(),
      QUERY_KEYS.tactics.base(),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.creditLog.base(),
    ],
    cachePrefixes: CACHE_BALANCE_ACCOUNTS,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.OBLIGATIONS_EDIT]: (params: RefreshConfigParams) => ({
    queryKeys: [
      ...QUERY_KEYS_ACCOUNTS_OBLIGATIONS_LOGS,
      ...(params.obligationId != null
        ? [
            QUERY_KEYS.obligations.detail(params.obligationId),
            QUERY_KEYS.obligations.payments(params.obligationId),
          ]
        : []),
    ],
    cachePrefixes: [CacheHelper.BALANCE_OBLIGATIONS_PREFIX, CacheHelper.BALANCE_ACCOUNTS_PREFIX],
    dispatchActions: ['getUserAccounts'],
  }),
  [InvalidationScope.INCOMES_TACTICS]: {
    queryKeys: [
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.incomes.base(),
      QUERY_KEYS.tactics.base(),
      QUERY_KEYS.logs.base(),
    ],
    cachePrefixes: CACHE_BALANCE_ACCOUNTS,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.SPENDINGS_TACTICS]: {
    queryKeys: [
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.spendings.base(),
      QUERY_KEYS.tactics.base(),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.creditLog.base(),
    ],
    cachePrefixes: CACHE_BALANCE_ACCOUNTS,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.ACTIVE]: {
    queryKeys: [
      QUERY_KEYS.invests.base(),
      QUERY_KEYS.archive.base(),
      QUERY_KEYS.repos.base(),
      QUERY_KEYS.properties.base(),
      QUERY_KEYS.active.depositPercentTypes.base(),
      QUERY_KEYS.cabinetValue.base(),
      QUERY_KEYS.goals.base(),
      QUERY_KEYS.valuations.base(),
      QUERY_KEYS.transferTrades.base(),
      QUERY_KEYS.transferTradesToApi.base(),
      QUERY_KEYS.activeFundItems.base(),
      QUERY_KEYS.active.base(),
      QUERY_KEYS.logs.base(),
    ],
    cachePrefixes: [
      CacheHelper.BALANCE_INVESTS_PREFIX,
      CacheHelper.BALANCE_PROPERTIES_PREFIX,
      CacheHelper.BALANCE_ACCOUNTS_PREFIX,
    ],
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.REPO]: {
    queryKeys: [
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.invests.base(),
      QUERY_KEYS.archive.base(),
      QUERY_KEYS.repos.base(),
      QUERY_KEYS.transferTrades.base(),
      QUERY_KEYS.transferTradesToApi.base(),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.creditLog.base(),
    ],
    cachePrefixes: CACHE_WELLBEING_BALANCE,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.DIVIDENDS]: {
    queryKeys: [
      QUERY_KEYS.dividends.base(),
      QUERY_KEYS.activesPayouts.base(),
      QUERY_KEYS.calendarPayments.base(),
      ...QUERY_KEYS_INVESTS_WITH_LOGS,
    ],
    cachePrefixes: CACHE_WELLBEING_BALANCE,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.COUPONS]: {
    queryKeys: [
      QUERY_KEYS.coupons.base(),
      QUERY_KEYS.activesPayouts.base(),
      QUERY_KEYS.calendarPayments.base(),
      ...QUERY_KEYS_INVESTS_WITH_LOGS,
    ],
    cachePrefixes: CACHE_WELLBEING_BALANCE,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.CATEGORY]: {
    queryKeys: [QUERY_KEYS.tactics.base()],
    cachePrefixes: [],
  },
  /** SubcategoryForm: обновляет список подкатегорий в Redux и инвалидирует списки доходов/расходов. */
  [InvalidationScope.SUBCATEGORY]: {
    queryKeys: [QUERY_KEYS.incomes.base(), QUERY_KEYS.spendings.base()],
    cachePrefixes: [],
    dispatchActions: ['getActiveSubcategories'],
  },
  [InvalidationScope.TRADE_PAYMENTS]: {
    queryKeys: QUERY_KEYS_INVESTS_WITH_LOGS,
    cachePrefixes: CACHE_WELLBEING_BALANCE,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.TRADE_COMMISSIONS]: {
    queryKeys: [
      QUERY_KEYS.tradeCommissions.base(),
      QUERY_KEYS.cabinetValue.base(),
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.creditLog.base(),
    ],
    cachePrefixes: CACHE_WELLBEING_BALANCE,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.TRANSFER_COMMISSIONS]: {
    queryKeys: [
      QUERY_KEYS.transferCommissions.base(),
      QUERY_KEYS.cabinetValue.base(),
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.creditLog.base(),
    ],
    cachePrefixes: CACHE_WELLBEING_BALANCE,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.SIMPLE_TRANSFER]: {
    queryKeys: QUERY_KEYS_ACCOUNTS_INVESTS_LOGS,
    cachePrefixes: CACHE_WELLBEING_BALANCE,
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.OBLIGATION_PAYMENTS]: {
    queryKeys: [
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.obligations.base(),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.activePayments.base(),
    ],
    cachePrefixes: [CacheHelper.BALANCE_OBLIGATIONS_PREFIX, CacheHelper.BALANCE_ACCOUNTS_PREFIX],
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.INSURANCE_ACTIONS]: {
    queryKeys: [
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.insurances.base(),
      QUERY_KEYS.invests.base(),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.creditLog.base(),
      QUERY_KEYS.cabinetValue.base(),
      QUERY_KEYS.dashboard.portfolioPiesBase(),
    ],
    cachePrefixes: [CacheHelper.BALANCE_PROPERTIES_PREFIX, CacheHelper.BALANCE_ACCOUNTS_PREFIX],
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.ALIMONY]: {
    queryKeys: QUERY_KEYS_ACCOUNTS_OBLIGATIONS_LOGS,
    cachePrefixes: [CacheHelper.BALANCE_OBLIGATIONS_PREFIX, CacheHelper.BALANCE_ACCOUNTS_PREFIX],
    dispatchActions: ['getUserAccounts'],
  },
  [InvalidationScope.CLIENT_SWITCH]: {
    // TODO(client-switch): queryKeys intentionally empty here because client switch now
    // uses refreshAllUserData() -> queryClient.clear() before invalidateByScope.
    // IndexedDB cleanup is done by CacheHelper.startAgain() in refreshAllUserData.
    queryKeys: [],
    cachePrefixes: CACHE_BALANCE_ACCOUNTS,
    dispatchActions: ['getUser', 'getAtonFiles', 'getUserAccounts'],
  },
  [InvalidationScope.BROKER_SYNC]: (_params: RefreshConfigParams) => ({
    queryKeys: QUERY_KEYS_BROKER_SYNC,
    cachePrefixes: CACHE_BROKER_SYNC,
    dispatchActions: ['getUserNotifications', 'getUserAccounts'],
  }),
  [InvalidationScope.BROKER_REPORT_FILES]: (_params: RefreshConfigParams) => ({
    queryKeys: QUERY_KEYS_BROKER_SYNC,
    cachePrefixes: CACHE_BROKER_SYNC,
    dispatchActions: ['getAtonFiles', 'getUserNotifications', 'getUserAccounts'],
  }),
  [InvalidationScope.ZENMONEY_SYNC]: (_params: RefreshConfigParams) => ({
    queryKeys: [
      QUERY_KEYS.incomes.base(),
      QUERY_KEYS.spendings.base(),
      QUERY_KEYS.tactics.base(),
      ...QUERY_KEYS_BROKER_SYNC,
    ],
    cachePrefixes: [...CACHE_BROKER_SYNC, CacheHelper.TACTICS_MONTH_PREFIX],
    dispatchActions: ['getUserNotifications', 'getUserAccounts'],
  }),
  [InvalidationScope.CHAT]: {
    queryKeys: [QUERY_KEYS.chat.base()],
    cachePrefixes: [],
  },
  [InvalidationScope.ACTIVE_GROUPS]: {
    queryKeys: [QUERY_KEYS.activeGroups.base()],
    cachePrefixes: [],
  },
  /** ClientsCreate/ClientsEdit: обновляет список клиентов через Redux-сагу getClients. */
  [InvalidationScope.CLIENT]: {
    queryKeys: [QUERY_KEYS.clients.base()],
    cachePrefixes: [],
    dispatchActions: ['getClients'],
  },
  /** Робоэдвайзер: при отключении (DisconnectModal) и при подключении (useRoboAdvisorConnection). Список и деталь по clientId/roboId. */
  [InvalidationScope.ROBOADVISOR]: (params: RefreshConfigParams) => ({
    queryKeys: [
      ...(params.clientId != null ? [QUERY_KEYS.roboadvisor.all(params.clientId)] : []),
      ...(params.roboId != null && params.clientId != null
        ? [QUERY_KEYS.roboadvisor.detail(params.roboId, params.clientId)]
        : []),
      ...(params.roboId != null ? [QUERY_KEYS.roboadvisor.status(params.roboId)] : []),
    ],
    cachePrefixes: [],
    dispatchActions: [],
  }),
  /** Отключение робоэдвайзера: обновляем списки без запросов к удаленному id (status/detail). */
  [InvalidationScope.ROBOADVISOR_DISCONNECT]: (params: RefreshConfigParams) => ({
    queryKeys: [
      ...(params.clientId != null ? [QUERY_KEYS.roboadvisor.all(params.clientId)] : []),
      ...(params.clientId != null
        ? [QUERY_KEYS.roboadvisor.notifications(params.clientId, undefined, undefined)]
        : []),
      QUERY_KEYS.integrationTokens.base(),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.creditLog.base(),
    ],
    cachePrefixes: CACHE_BALANCE_ACCOUNTS,
    dispatchActions: ['getUserAccounts'],
  }),
  /** Вывод средств робоэдвайзера (WithdrawModal). Деталь, операции, баланс, нотификации. */
  [InvalidationScope.ROBOADVISOR_WITHDRAW]: (params: RefreshConfigParams) => ({
    queryKeys: [
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.cabinetValue.base(),
      ...(params.roboId != null && params.clientId != null
        ? [QUERY_KEYS.roboadvisor.detail(params.roboId, params.clientId)]
        : []),
      ...(params.roboId != null ? [QUERY_KEYS.roboadvisor.status(params.roboId)] : []),
      ...(params.roboId != null && params.clientId != null
        ? [QUERY_KEYS.roboadvisor.trades(params.roboId, params.clientId)]
        : []),
      ...(params.roboId != null && params.currency != null
        ? [QUERY_KEYS.roboadvisor.balance(params.roboId, params.currency)]
        : []),
      ...(params.clientId != null
        ? [QUERY_KEYS.roboadvisor.notifications(params.clientId, undefined, undefined)]
        : []),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.creditLog.base(),
    ],
    cachePrefixes: CACHE_BALANCE_ACCOUNTS,
    dispatchActions: ['getUserAccounts'],
  }),
  /** Сохранение данных подписки (ContractStep). Паспортные данные для визарда. */
  [InvalidationScope.ROBOADVISOR_USER_PASSPORT]: {
    queryKeys: [QUERY_KEYS.roboadvisor.userPassport()],
    cachePrefixes: [],
    dispatchActions: ['getUser'],
  },
  [InvalidationScope.ROBOADVISOR_USER_PASSPORT_QUERY_ONLY]: {
    queryKeys: [QUERY_KEYS.roboadvisor.userPassport()],
    cachePrefixes: [],
    dispatchActions: [],
  },
  /** Создание кастомного банка (AccountForm): инвалидация только списка банков по clientId. */
  [InvalidationScope.BANKS_LIST]: (params: RefreshConfigParams) => ({
    queryKeys: params.clientId != null ? [QUERY_KEYS.banks.all(params.clientId)] : [],
    cachePrefixes: [],
    dispatchActions: [],
  }),
  /** Создание кастомного брокера (AccountForm): инвалидация только списка брокеров по clientId. */
  [InvalidationScope.BROKERS_LIST]: (params: RefreshConfigParams) => ({
    queryKeys: params.clientId != null ? [QUERY_KEYS.brokers.all(params.clientId)] : [],
    cachePrefixes: [],
    dispatchActions: [],
  }),
  [InvalidationScope.ADVISOR_BIND]: {
    queryKeys: [],
    cachePrefixes: CACHE_BALANCE_ACCOUNTS,
    dispatchActions: ['getUser', 'getUserAccounts'],
  },
  [InvalidationScope.INTEGRATIONS_DELETE_ACCOUNT]: {
    queryKeys: [
      QUERY_KEYS.integrationTokens.base(),
      ...QUERY_KEYS_INVESTS_WITH_LOGS,
      QUERY_KEYS.incomes.base(),
      QUERY_KEYS.spendings.base(),
      QUERY_KEYS.tactics.base(),
      QUERY_KEYS.dividends.base(),
      QUERY_KEYS.coupons.base(),
      QUERY_KEYS.calendarPayments.base(),
      QUERY_KEYS.obligations.base(),
      QUERY_KEYS.properties.base(),
      QUERY_KEYS.goals.base(),
      QUERY_KEYS.goalItems.base(),
      QUERY_KEYS.insurances.base(),
      QUERY_KEYS.activePayments.base(),
      QUERY_KEYS.notifications.base(),
    ],
    cachePrefixes: [
      ...CACHE_WELLBEING_BALANCE,
      CacheHelper.TACTICS_MONTH_PREFIX,
      CacheHelper.BALANCE_OBLIGATIONS_PREFIX,
      CacheHelper.BALANCE_PROPERTIES_PREFIX,
    ],
    dispatchActions: ['resetAvailableTypes', 'getUserAccounts'],
  },
  [InvalidationScope.INTEGRATION_TOKENS]: {
    queryKeys: [QUERY_KEYS.integrationTokens.base()],
    cachePrefixes: [],
    dispatchActions: [],
  },
  [InvalidationScope.INVESTS_ACTIVES_UPDATED]: {
    queryKeys: [
      QUERY_KEYS.accounts.base(),
      QUERY_KEYS.archive.base(),
      QUERY_KEYS.repos.base(),
      QUERY_KEYS.transferTrades.base(),
      QUERY_KEYS.transferTradesToApi.base(),
      QUERY_KEYS.invests.forGoalBindingBase(),
      QUERY_KEYS.invests.cabinetInvestGridActivesBase(),
      QUERY_KEYS.invests.caseNumbersBase(),
      QUERY_KEYS.cabinetValue.base(),
      QUERY_KEYS.logs.base(),
      QUERY_KEYS.creditLog.base(),
      QUERY_KEYS.notifications.base(),
    ],
    cachePrefixes: [
      CacheHelper.BALANCE_INVESTS_PREFIX,
      CacheHelper.BALANCE_ACCOUNTS_PREFIX,
      CacheHelper.BALANCE_PROPERTIES_PREFIX,
    ],
    dispatchActions: ['getUserAccounts'],
  },
};

/**
 * Возвращает конфиг по scope: { queryKeys, cachePrefixes, dispatchActions? }.
 */
export function getRefreshConfig(scope: string, params: RefreshConfigParams = {}): ScopeConfig {
  const config = SCOPE_CONFIG[scope];
  if (!config) {
    return { queryKeys: [], cachePrefixes: [] };
  }
  return typeof config === 'function' ? config(params) : config;
}

/**
 * Возвращает массив queryKeys для инвалидации TanStack Query по scope.
 */
export function getQueryKeysForScope(scope: string, params: RefreshConfigParams = {}): unknown[] {
  return getRefreshConfig(scope, params).queryKeys;
}

/**
 * Возвращает массив префиксов IndexedDB для очистки по scope.
 */
export function getIndexedDBCachePrefixesForScope(
  scope: string,
  params: RefreshConfigParams = {}
): string[] {
  return getRefreshConfig(scope, params).cachePrefixes;
}
